/**
 * Expense catalog with icons, colors, and Canadian-typical default amounts.
 *
 * Each item:
 *  - key: unique identifier (used for i18n lookup + state matching)
 *  - icon: MUI icon name string (resolved in component)
 *  - defaultAmount: typical monthly amount for a newcomer in Canada (CAD)
 *  - frequency: default frequency
 *
 * Each category:
 *  - key: unique identifier
 *  - color: hex color for the category
 *  - icon: MUI icon name string
 *  - items: array of expense items
 */

// ─── FIXED EXPENSE CATEGORIES ────────────────────────────────────────

export const FIXED_EXPENSE_CATALOG = {
  housing: {
    color: '#1E3A5F',
    iconName: 'Home',
    items: [
      { key: 'rent', defaultAmount: 1500, frequency: 'monthly' },
      { key: 'homeInsurance', defaultAmount: 80, frequency: 'monthly' },
      { key: 'propertyTax', defaultAmount: 250, frequency: 'monthly' },
      { key: 'condoFees', defaultAmount: 350, frequency: 'monthly' },
    ],
  },
  utilities: {
    color: '#7D5A00',
    iconName: 'Bolt',
    items: [
      { key: 'electricity', defaultAmount: 75, frequency: 'monthly' },
      { key: 'gasHeating', defaultAmount: 60, frequency: 'monthly' },
      { key: 'internet', defaultAmount: 65, frequency: 'monthly' },
      { key: 'phone', defaultAmount: 50, frequency: 'monthly' },
      { key: 'water', defaultAmount: 40, frequency: 'monthly' },
    ],
  },
  transportation: {
    color: '#2E7D32',
    iconName: 'DirectionsCar',
    items: [
      { key: 'carPayment', defaultAmount: 450, frequency: 'monthly' },
      { key: 'carInsurance', defaultAmount: 180, frequency: 'monthly' },
      { key: 'transitPass', defaultAmount: 130, frequency: 'monthly' },
      { key: 'gasoline', defaultAmount: 150, frequency: 'monthly' },
      { key: 'parking', defaultAmount: 100, frequency: 'monthly' },
    ],
  },
  insurance: {
    color: '#7B1FA2',
    iconName: 'Security',
    items: [
      { key: 'lifeInsurance', defaultAmount: 40, frequency: 'monthly' },
      { key: 'dentalInsurance', defaultAmount: 35, frequency: 'monthly' },
      { key: 'healthInsurance', defaultAmount: 60, frequency: 'monthly' },
    ],
  },
  subscriptions: {
    color: '#C62828',
    iconName: 'Subscriptions',
    items: [
      { key: 'streaming', defaultAmount: 25, frequency: 'monthly' },
      { key: 'gym', defaultAmount: 45, frequency: 'monthly' },
      { key: 'appSubscription', defaultAmount: 15, frequency: 'monthly' },
    ],
  },
  childcare: {
    color: '#00838F',
    iconName: 'ChildCare',
    items: [
      { key: 'daycare', defaultAmount: 800, frequency: 'monthly' },
      { key: 'childActivities', defaultAmount: 100, frequency: 'monthly' },
    ],
  },
};

// ─── VARIABLE EXPENSE CATEGORIES ─────────────────────────────────────

export const VARIABLE_EXPENSE_CATALOG = {
  food: {
    color: '#7D5A00',
    iconName: 'ShoppingCart',
    items: [
      { key: 'groceries', defaultAmount: 500, frequency: 'monthly' },
      { key: 'restaurants', defaultAmount: 150, frequency: 'monthly' },
      { key: 'coffee', defaultAmount: 40, frequency: 'monthly' },
    ],
  },
  personal: {
    color: '#7B1FA2',
    iconName: 'Person',
    items: [
      { key: 'clothing', defaultAmount: 80, frequency: 'monthly' },
      { key: 'personalCare', defaultAmount: 50, frequency: 'monthly' },
      { key: 'education', defaultAmount: 100, frequency: 'monthly' },
    ],
  },
  entertainment: {
    color: '#C62828',
    iconName: 'TheaterComedy',
    items: [
      { key: 'goingOut', defaultAmount: 100, frequency: 'monthly' },
      { key: 'hobbies', defaultAmount: 60, frequency: 'monthly' },
      { key: 'travel', defaultAmount: 150, frequency: 'monthly' },
    ],
  },
  health: {
    color: '#2E7D32',
    iconName: 'LocalHospital',
    items: [
      { key: 'medications', defaultAmount: 30, frequency: 'monthly' },
      { key: 'doctorVisits', defaultAmount: 50, frequency: 'monthly' },
    ],
  },
  other: {
    color: '#5F6B7A',
    iconName: 'MoreHoriz',
    items: [
      { key: 'remittances', defaultAmount: 200, frequency: 'monthly' },
      { key: 'gifts', defaultAmount: 50, frequency: 'monthly' },
      { key: 'miscellaneous', defaultAmount: 75, frequency: 'monthly' },
      { key: 'petCare', defaultAmount: 80, frequency: 'monthly' },
    ],
  },
};

// ─── PRESETS ──────────────────────────────────────────────────────────
// Each preset maps expense item keys → amount overrides (null = excluded)

export const EXPENSE_PRESETS = {
  newcomerSolo: {
    fixed: {
      rent: 1400, homeInsurance: null, propertyTax: null, condoFees: null,
      electricity: 60, gasHeating: 50, internet: 55, phone: 45, water: null,
      carPayment: null, carInsurance: null, transitPass: 130, gasoline: null, parking: null,
      lifeInsurance: null, dentalInsurance: null, healthInsurance: null,
      streaming: 20, gym: 40, appSubscription: null,
      daycare: null, childActivities: null,
    },
    variable: {
      groceries: 350, restaurants: 80, coffee: 30,
      clothing: 50, personalCare: 30, education: null,
      goingOut: 60, hobbies: 40, travel: null,
      medications: null, doctorVisits: null,
      remittances: 150, gifts: 30, miscellaneous: 50, petCare: null,
    },
  },
  family: {
    fixed: {
      rent: 1800, homeInsurance: 80, propertyTax: null, condoFees: null,
      electricity: 90, gasHeating: 70, internet: 70, phone: 90, water: 45,
      carPayment: 450, carInsurance: 200, transitPass: null, gasoline: 160, parking: null,
      lifeInsurance: 45, dentalInsurance: 40, healthInsurance: 60,
      streaming: 30, gym: 50, appSubscription: null,
      daycare: 800, childActivities: 120,
    },
    variable: {
      groceries: 700, restaurants: 120, coffee: 40,
      clothing: 120, personalCare: 60, education: 80,
      goingOut: 80, hobbies: 50, travel: 100,
      medications: 30, doctorVisits: 50,
      remittances: 200, gifts: 60, miscellaneous: 80, petCare: null,
    },
  },
  student: {
    fixed: {
      rent: 900, homeInsurance: null, propertyTax: null, condoFees: null,
      electricity: null, gasHeating: null, internet: 50, phone: 35, water: null,
      carPayment: null, carInsurance: null, transitPass: 100, gasoline: null, parking: null,
      lifeInsurance: null, dentalInsurance: null, healthInsurance: null,
      streaming: 15, gym: 30, appSubscription: 10,
      daycare: null, childActivities: null,
    },
    variable: {
      groceries: 250, restaurants: 60, coffee: 25,
      clothing: 30, personalCare: 20, education: 200,
      goingOut: 50, hobbies: 30, travel: null,
      medications: null, doctorVisits: null,
      remittances: 100, gifts: 20, miscellaneous: 30, petCare: null,
    },
  },
};

// ─── i18n LABELS ─────────────────────────────────────────────────────
// Maps item keys to { es, en, fr } labels

export const EXPENSE_LABELS = {
  // Category names
  categories: {
    housing: { es: 'Vivienda', en: 'Housing', fr: 'Logement' },
    utilities: { es: 'Servicios', en: 'Utilities', fr: 'Services' },
    transportation: { es: 'Transporte', en: 'Transportation', fr: 'Transport' },
    insurance: { es: 'Seguros', en: 'Insurance', fr: 'Assurances' },
    subscriptions: { es: 'Suscripciones', en: 'Subscriptions', fr: 'Abonnements' },
    childcare: { es: 'Hijos', en: 'Childcare', fr: 'Enfants' },
    food: { es: 'Comida', en: 'Food', fr: 'Alimentation' },
    personal: { es: 'Personal', en: 'Personal', fr: 'Personnel' },
    entertainment: { es: 'Entretenimiento', en: 'Entertainment', fr: 'Divertissement' },
    health: { es: 'Salud', en: 'Health', fr: 'Sant\u00e9' },
    other: { es: 'Otros', en: 'Other', fr: 'Autres' },
  },
  // Item names
  items: {
    rent: { es: 'Alquiler / Hipoteca', en: 'Rent / Mortgage', fr: 'Loyer / Hypoth\u00e8que' },
    homeInsurance: { es: 'Seguro de hogar', en: 'Home Insurance', fr: 'Assurance habitation' },
    propertyTax: { es: 'Impuesto de propiedad', en: 'Property Tax', fr: 'Taxe fonci\u00e8re' },
    condoFees: { es: 'Gastos de condo', en: 'Condo Fees', fr: 'Frais de condo' },
    electricity: { es: 'Electricidad', en: 'Electricity', fr: '\u00c9lectricit\u00e9' },
    gasHeating: { es: 'Gas / Calefacci\u00f3n', en: 'Gas / Heating', fr: 'Gaz / Chauffage' },
    internet: { es: 'Internet', en: 'Internet', fr: 'Internet' },
    phone: { es: 'Celular', en: 'Phone', fr: 'Cellulaire' },
    water: { es: 'Agua', en: 'Water', fr: 'Eau' },
    carPayment: { es: 'Pago del auto', en: 'Car Payment', fr: 'Paiement auto' },
    carInsurance: { es: 'Seguro del auto', en: 'Car Insurance', fr: 'Assurance auto' },
    transitPass: { es: 'Transporte p\u00fablico', en: 'Transit Pass', fr: 'Passe de transport' },
    gasoline: { es: 'Gasolina', en: 'Gasoline', fr: 'Essence' },
    parking: { es: 'Estacionamiento', en: 'Parking', fr: 'Stationnement' },
    lifeInsurance: { es: 'Seguro de vida', en: 'Life Insurance', fr: 'Assurance vie' },
    dentalInsurance: { es: 'Seguro dental', en: 'Dental Insurance', fr: 'Assurance dentaire' },
    healthInsurance: { es: 'Seguro m\u00e9dico', en: 'Health Insurance', fr: 'Assurance maladie' },
    streaming: { es: 'Streaming', en: 'Streaming', fr: 'Streaming' },
    gym: { es: 'Gimnasio', en: 'Gym', fr: 'Gym' },
    appSubscription: { es: 'Apps / Software', en: 'Apps / Software', fr: 'Apps / Logiciels' },
    daycare: { es: 'Guarder\u00eda', en: 'Daycare', fr: 'Garderie' },
    childActivities: { es: 'Actividades ni\u00f1os', en: 'Child Activities', fr: 'Activit\u00e9s enfants' },
    groceries: { es: 'Supermercado', en: 'Groceries', fr: '\u00c9picerie' },
    restaurants: { es: 'Restaurantes / Delivery', en: 'Restaurants / Delivery', fr: 'Restaurants / Livraison' },
    coffee: { es: 'Caf\u00e9', en: 'Coffee', fr: 'Caf\u00e9' },
    clothing: { es: 'Ropa', en: 'Clothing', fr: 'V\u00eatements' },
    personalCare: { es: 'Cuidado personal', en: 'Personal Care', fr: 'Soins personnels' },
    education: { es: 'Educaci\u00f3n', en: 'Education', fr: '\u00c9ducation' },
    goingOut: { es: 'Salidas', en: 'Going Out', fr: 'Sorties' },
    hobbies: { es: 'Hobbies', en: 'Hobbies', fr: 'Loisirs' },
    travel: { es: 'Viajes', en: 'Travel', fr: 'Voyages' },
    medications: { es: 'Medicamentos', en: 'Medications', fr: 'M\u00e9dicaments' },
    doctorVisits: { es: 'Consultas m\u00e9dicas', en: 'Doctor Visits', fr: 'Consultations m\u00e9dicales' },
    remittances: { es: 'Remesas', en: 'Remittances', fr: 'Transferts' },
    gifts: { es: 'Regalos', en: 'Gifts', fr: 'Cadeaux' },
    miscellaneous: { es: 'Imprevistos', en: 'Miscellaneous', fr: 'Impr\u00e9vus' },
    petCare: { es: 'Mascotas', en: 'Pet Care', fr: 'Animaux' },
  },
};

// ─── PRESET LABELS ───────────────────────────────────────────────────

export const PRESET_LABELS = {
  newcomerSolo: { es: 'Reci\u00e9n llegado solo', en: 'Newcomer solo', fr: 'Nouvel arrivant seul' },
  family: { es: 'Familia', en: 'Family', fr: 'Famille' },
  student: { es: 'Estudiante', en: 'Student', fr: '\u00c9tudiant' },
};

// ─── LEGACY EXPORTS (keep backward compat for breakdown editor) ──────

export const FIXED_EXPENSE_CATEGORIES = {
  es: Object.entries(FIXED_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.es || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.es || item.key),
  })),
  en: Object.entries(FIXED_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.en || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.en || item.key),
  })),
  fr: Object.entries(FIXED_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.fr || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.fr || item.key),
  })),
};

export const VARIABLE_EXPENSE_CATEGORIES = {
  es: Object.entries(VARIABLE_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.es || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.es || item.key),
  })),
  en: Object.entries(VARIABLE_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.en || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.en || item.key),
  })),
  fr: Object.entries(VARIABLE_EXPENSE_CATALOG).map(([catKey, cat]) => ({
    category: EXPENSE_LABELS.categories[catKey]?.fr || catKey,
    suggestions: cat.items.map((item) => EXPENSE_LABELS.items[item.key]?.fr || item.key),
  })),
};
